import type { ExpressionSpecification } from "mapbox-gl";

import {
  FillPatternKey,
  LayerStyle,
  LineStyleKey,
  StoryLayerKey,
} from "../../types/storyMap";
import type { CirclePaint, FillPaint, LinePaint, LineLayout } from "./storyLayers";

/**
 * Per-entry display overrides: which controls a layer offers, how a
 * LayerStyle folds into mapbox paints, and the fill-pattern images.
 *
 * Patterns are registered on the map as sprite images (mapbox `fill-pattern`
 * ignores `fill-color`, so each pattern is baked in the chosen colour). The
 * image id encodes pattern + colour, so StoryMapCanvas registers each once.
 */

/** Which style controls a layer key honours. */
export type StyleCapability =
  | "fill" // color + fillOpacity + fillPattern
  | "fillOpacity" // opacity only (per-feature-coloured polygons)
  | "line" // lineColor + lineWidth + lineStyle + lineOpacity
  | "point" // pointRadius (colour follows `color`)
  | "raster"; // opacity

export const STYLE_CAPABILITIES: Record<StoryLayerKey, StyleCapability[]> = {
  boundary: ["line"],
  properties: ["line"],
  treatments: ["fill", "line"],
  assets: ["fill"],
  fuel_load: ["fillOpacity"],
  // The 3D stand has fixed symbology; its parts toggle in the entry config.
  fuel_3d: [],
  vegetation: ["fillOpacity"],
  fire_history: ["fillOpacity"],
  ignitions: ["fill", "line", "point"],
  custom_layers: ["fill", "line", "point"],
  risk_from: ["fillOpacity", "line", "point"],
  risk_to: ["raster"],
  inspection: ["fillOpacity"],
  // Freehand features carry their own per-feature styles.
  freehand: [],
  // Weather symbology is fixed (white labels/arrows, speed-coloured particles).
  weather: [],
};

export const LINE_STYLE_LABELS: Record<LineStyleKey, string> = {
  solid: "Solid",
  dashed: "Dashed",
  dotted: "Dotted",
};

export const FILL_PATTERN_LABELS: Record<FillPatternKey, string> = {
  none: "Solid",
  hatch: "Hatched",
  hatch_back: "Hatched (back)",
  crosshatch: "Cross-hatched",
  dots: "Dotted",
};

/** Dash arrays in line-width units (round caps make "dotted" real dots). */
export function lineDashArray(style: LineStyleKey | undefined): number[] | null {
  switch (style) {
    case "dashed":
      return [2, 2];
    case "dotted":
      return [0, 2];
    default:
      return null;
  }
}

/** Layout companion for a dashed/dotted line (round caps + joins). */
export function lineLayoutFor(style: LineStyleKey | undefined): LineLayout | undefined {
  if (!style || style === "solid") return undefined;
  return { "line-cap": "round", "line-join": "round" };
}

const HEX_RE = /^#([0-9a-fA-F]{6})$/;

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_RE.test(value);
}

function clamp01(v: number | undefined): number | undefined {
  if (v == null || !Number.isFinite(v)) return undefined;
  return Math.min(1, Math.max(0, v));
}

/** Effective outline colour: explicit lineColor, else the fill colour. */
export function effectiveLineColor(style: LayerStyle | undefined): string | undefined {
  if (!style) return undefined;
  if (isHexColor(style.lineColor)) return style.lineColor;
  if (isHexColor(style.color)) return style.color;
  return undefined;
}

// ---------------------------------------------------------------------------
// Pattern images
// ---------------------------------------------------------------------------

const PATTERN_SIZE = 16;

export function patternImageId(pattern: FillPatternKey, color: string): string {
  return `story-pattern-${pattern}-${color.replace("#", "").toLowerCase()}`;
}

/**
 * A tileable PATTERN_SIZE² image of the pattern in `color` (transparent
 * background), for `map.addImage(..., { pixelRatio: 1 })`. Null in non-DOM
 * environments or for "none".
 */
export function buildPatternImage(
  pattern: FillPatternKey,
  color: string
): ImageData | null {
  if (pattern === "none" || typeof document === "undefined") return null;
  const size = PATTERN_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = "square";
  const diagonal = (backslash: boolean) => {
    // Three strokes so the tile wraps seamlessly at the edges.
    for (const offset of [-size, 0, size]) {
      ctx.beginPath();
      if (backslash) {
        ctx.moveTo(offset, 0);
        ctx.lineTo(offset + size, size);
      } else {
        ctx.moveTo(offset, size);
        ctx.lineTo(offset + size, 0);
      }
      ctx.stroke();
    }
  };
  switch (pattern) {
    case "hatch":
      diagonal(false);
      break;
    case "hatch_back":
      diagonal(true);
      break;
    case "crosshatch":
      diagonal(false);
      diagonal(true);
      break;
    case "dots":
      ctx.beginPath();
      ctx.arc(size / 4, size / 4, 1.8, 0, Math.PI * 2);
      ctx.arc((3 * size) / 4, (3 * size) / 4, 1.8, 0, Math.PI * 2);
      ctx.fill();
      break;
  }
  return ctx.getImageData(0, 0, size, size);
}

/** CSS background for a legend/chip swatch of the pattern in `color`. */
export function patternCss(pattern: FillPatternKey | undefined, color: string): string {
  switch (pattern) {
    case "hatch":
      return `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 6px)`;
    case "hatch_back":
      return `repeating-linear-gradient(-45deg, ${color} 0 2px, transparent 2px 6px)`;
    case "crosshatch":
      return `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 6px), repeating-linear-gradient(-45deg, ${color} 0 2px, transparent 2px 6px)`;
    case "dots":
      return `radial-gradient(${color} 1.5px, transparent 1.6px) 0 0 / 6px 6px`;
    default:
      return color;
  }
}

// ---------------------------------------------------------------------------
// Paint appliers — fold a LayerStyle into a base paint. Pure; base untouched.
// ---------------------------------------------------------------------------

export interface StyledFill {
  paint: FillPaint;
  /** Pattern image to register (null = flat fill). */
  pattern: { id: string; data: ImageData } | null;
}

/**
 * Fill paint with the style's colour/opacity/pattern applied. `allowColor`
 * false keeps the base colour expression (per-feature-coloured layers) but
 * still honours opacity. Patterns need a concrete colour: the style's, else
 * `patternFallbackColor`.
 */
export function styledFillPaint(
  base: FillPaint,
  style: LayerStyle | undefined,
  opts: { allowColor: boolean; patternFallbackColor?: string }
): StyledFill {
  const paint: FillPaint = { ...base };
  let pattern: StyledFill["pattern"] = null;
  if (!style) return { paint, pattern };
  if (opts.allowColor && isHexColor(style.color)) {
    paint["fill-color"] = style.color;
    if ("fill-outline-color" in base) paint["fill-outline-color"] = style.color;
  }
  const fillOpacity = clamp01(style.fillOpacity);
  if (fillOpacity != null) {
    // Vector/fuel layers gate opacity on a per-feature test; keep the gate
    // and swap the visible value.
    const baseOpacity = base["fill-opacity"];
    if (Array.isArray(baseOpacity) && baseOpacity[0] === "case") {
      const expr = [...(baseOpacity as unknown[])] as unknown[];
      expr[expr.length - 1] = fillOpacity;
      paint["fill-opacity"] = expr as unknown as ExpressionSpecification;
    } else {
      paint["fill-opacity"] = fillOpacity;
    }
  }
  const patternKey = style.fillPattern;
  if (patternKey && patternKey !== "none") {
    const color = isHexColor(style.color)
      ? style.color
      : opts.patternFallbackColor;
    const data = color ? buildPatternImage(patternKey, color) : null;
    if (color && data) {
      pattern = { id: patternImageId(patternKey, color), data };
      paint["fill-pattern"] = pattern.id;
      // A pattern's opacity is the pattern image's own alpha times this.
      delete paint["fill-outline-color"];
    }
  }
  return { paint, pattern };
}

export interface StyledLine {
  paint: LinePaint;
  layout?: LineLayout;
}

/** Line paint with colour/width/dash/opacity applied. */
export function styledLinePaint(
  base: LinePaint,
  style: LayerStyle | undefined,
  opts: { allowColor: boolean }
): StyledLine {
  const paint: LinePaint = { ...base };
  if (!style) return { paint };
  const color = effectiveLineColor(style);
  if (opts.allowColor && color) paint["line-color"] = color;
  if (style.lineWidth != null && Number.isFinite(style.lineWidth)) {
    paint["line-width"] = Math.max(0, style.lineWidth);
  }
  const lineOpacity = clamp01(style.lineOpacity);
  if (lineOpacity != null) paint["line-opacity"] = lineOpacity;
  const dash = lineDashArray(style.lineStyle);
  if (dash) paint["line-dasharray"] = dash;
  else if (style.lineStyle === "solid") delete paint["line-dasharray"];
  return { paint, layout: lineLayoutFor(style.lineStyle) };
}

/** Circle paint with colour/radius/opacity applied. */
export function styledCirclePaint(
  base: CirclePaint,
  style: LayerStyle | undefined,
  opts: { allowColor: boolean }
): CirclePaint {
  const paint: CirclePaint = { ...base };
  if (!style) return paint;
  if (opts.allowColor && isHexColor(style.color)) paint["circle-color"] = style.color;
  if (style.pointRadius != null && Number.isFinite(style.pointRadius)) {
    paint["circle-radius"] = Math.max(0, style.pointRadius);
  }
  const opacity = clamp01(style.fillOpacity);
  if (opacity != null) paint["circle-opacity"] = opacity;
  return paint;
}

/** True when the style has any field set (chips show a "styled" mark). */
export function hasStyleOverrides(style: LayerStyle | undefined): boolean {
  if (!style) return false;
  return Object.values(style).some((v) => v != null && v !== "" && v !== "none");
}

// ---------------------------------------------------------------------------
// Freehand per-feature defaults
// ---------------------------------------------------------------------------

export const FREEHAND_DEFAULT_COLOR = "#ff3d6e";
export const FREEHAND_DEFAULT_WIDTH = 3;
export const FREEHAND_DEFAULT_FILL_OPACITY = 0.25;

/** Fixed swatch palette offered by the drawing tool and style editors. */
export const SWATCH_PALETTE: string[] = [
  "#ff3d6e",
  "#ff8a00",
  "#ffd600",
  "#00d4ad",
  "#2bb3ff",
  "#7c4dff",
  "#ffffff",
  "#212b37",
];
